import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { metricsService } from '../services/endpoints/metricsService';
import { pedService } from '../services/endpoints/pedService';
import { useCoachView } from '../contexts/CoachViewContext';
import { DailyMetrics } from '../types';
import { format, subDays } from 'date-fns';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

type PresetDays = 7 | 14 | 30 | 60 | 90 | 180 | 365;

const Analytics: React.FC = () => {
  const [selectedDays, setSelectedDays] = useState<PresetDays | 'custom'>(90);
  const [customStart, setCustomStart] = useState(format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [customEnd, setCustomEnd] = useState(format(new Date(), 'yyyy-MM-dd'));
  const { viewAs } = useCoachView();

  const startDate = selectedDays === 'custom'
    ? customStart
    : format(subDays(new Date(), selectedDays), 'yyyy-MM-dd');
  const endDate = selectedDays === 'custom'
    ? customEnd
    : format(new Date(), 'yyyy-MM-dd');

  const [aasSelectedDays, setAasSelectedDays] = useState<PresetDays | 'custom'>(90);
  const [aasCustomStart, setAasCustomStart] = useState(format(subDays(new Date(), 90), 'yyyy-MM-dd'));
  const [aasCustomEnd, setAasCustomEnd] = useState(format(new Date(), 'yyyy-MM-dd'));

  const aasStartDate = aasSelectedDays === 'custom'
    ? aasCustomStart
    : format(subDays(new Date(), aasSelectedDays), 'yyyy-MM-dd');
  const aasEndDate = aasSelectedDays === 'custom'
    ? aasCustomEnd
    : format(new Date(), 'yyyy-MM-dd');

  const { data: metrics = [], isLoading } = useQuery({
    queryKey: ['analytics-metrics', selectedDays, customStart, customEnd, viewAs],
    queryFn: () => metricsService.getMetrics(startDate, endDate, undefined, viewAs ?? undefined),
    enabled: !!startDate && !!endDate,
  });

  const { data: pedLogs = [] } = useQuery({
    queryKey: ['analytics-ped-logs', selectedDays, customStart, customEnd, viewAs],
    queryFn: () => pedService.getLogs({ startDate, endDate, viewAs: viewAs ?? undefined }),
    enabled: !!startDate && !!endDate,
  });

  const { data: aasLogs = [] } = useQuery({
    queryKey: ['analytics-aas-logs', aasSelectedDays, aasCustomStart, aasCustomEnd, viewAs],
    queryFn: () => pedService.getLogs({ startDate: aasStartDate, endDate: aasEndDate, viewAs: viewAs ?? undefined }),
    enabled: !!aasStartDate && !!aasEndDate,
  });

  const { data: cycles = [] } = useQuery<import('../types').PEDCycle[]>({
    queryKey: ['ped-cycles', viewAs],
    queryFn: () => pedService.getCycles(viewAs ?? undefined),
  });

  // Sort metrics by date ascending
  const sortedMetrics = useMemo(() =>
    [...metrics].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()),
    [metrics]
  );

  const labels = sortedMetrics.map(m => format(new Date(m.date), 'MMM d'));

  // Summary stats
  const summary = useMemo(() => {
    if (sortedMetrics.length < 2) return null;
    const first = sortedMetrics[0];
    const last = sortedMetrics[sortedMetrics.length - 1];
    const mid = Math.floor(sortedMetrics.length / 2);

    const weightChange = (last.weight ?? 0) - (first.weight ?? 0);

    const withHrv = sortedMetrics.filter(m => m.overnightHrv != null);
    const avgHrv = withHrv.length > 0
      ? Math.round(withHrv.reduce((sum, m) => sum + (m.overnightHrv ?? 0), 0) / withHrv.length)
      : null;

    const withRhr = sortedMetrics.filter(m => m.overnightRestingHeartRate != null);
    const avgRestingHr = withRhr.length > 0
      ? Math.round(withRhr.reduce((sum, m) => sum + (m.overnightRestingHeartRate ?? 0), 0) / withRhr.length)
      : null;

    const withBP = sortedMetrics.filter(m => m.bloodPressureSystolic);
    const avgSystolic = withBP.length > 0
      ? Math.round(withBP.reduce((sum, m) => sum + (m.bloodPressureSystolic ?? 0), 0) / withBP.length)
      : null;
    const avgDiastolic = withBP.length > 0
      ? Math.round(withBP.reduce((sum, m) => sum + (m.bloodPressureDiastolic ?? 0), 0) / withBP.length)
      : null;

    const withSleep = sortedMetrics.filter(m => m.sleepHours);
    const avgSleep = withSleep.length > 0
      ? (withSleep.reduce((sum, m) => sum + (m.sleepHours ?? 0), 0) / withSleep.length).toFixed(1)
      : null;

    const withWellbeing = sortedMetrics.filter(m => m.wellbeingScore);
    const avgWellbeing = withWellbeing.length > 0
      ? (withWellbeing.reduce((sum, m) => sum + (m.wellbeingScore ?? 0), 0) / withWellbeing.length).toFixed(1)
      : null;

    const withSteps = sortedMetrics.filter(m => m.stepsYesterday != null);
    const avgSteps = withSteps.length > 0
      ? Math.round(withSteps.reduce((sum, m) => sum + (m.stepsYesterday ?? 0), 0) / withSteps.length)
      : null;

    return { weightChange, avgHrv, avgRestingHr, avgSystolic, avgDiastolic, avgSleep, avgWellbeing, avgSteps };
  }, [sortedMetrics]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        labels: { color: '#9ca3af' },
      },
    },
    scales: {
      x: {
        ticks: { color: '#6b7280', maxTicksLimit: 12 },
        grid: { color: 'rgba(75, 85, 99, 0.3)' },
      },
      y: {
        ticks: { color: '#6b7280' },
        grid: { color: 'rgba(75, 85, 99, 0.3)' },
      },
    },
  };

  // ===== Weight Trend Chart =====
  const weightSmoothed = sortedMetrics.map((_, i) => {
    const window = sortedMetrics.slice(Math.max(0, i - 6), i + 1).filter(m => m.weight != null);
    if (window.length === 0) return null;
    return window.reduce((sum, m) => sum + (m.weight ?? 0), 0) / window.length;
  });

  const bodyCompData = {
    labels,
    datasets: [
      {
        label: 'Weight (lbs)',
        data: sortedMetrics.map(m => m.weight ?? null),
        borderColor: '#d4a843',
        backgroundColor: 'rgba(212, 168, 67, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      },
      {
        label: '7d Avg',
        data: weightSmoothed,
        borderColor: '#22c55e',
        backgroundColor: 'transparent',
        fill: false,
        tension: 0.4,
        pointRadius: 0,
        borderWidth: 2,
        spanGaps: true,
      },
    ],
  };

  // ===== HRV & Resting HR Chart =====
  const hrvRhrData = {
    labels,
    datasets: [
      {
        label: 'HRV (ms)',
        data: sortedMetrics.map(m => m.overnightHrv ?? null),
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20, 184, 166, 0.12)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
        yAxisID: 'y',
      },
      {
        label: 'Resting HR (bpm)',
        data: sortedMetrics.map(m => m.overnightRestingHeartRate ?? null),
        borderColor: '#f97316',
        backgroundColor: 'transparent',
        fill: false,
        borderDash: [5, 3],
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
        yAxisID: 'y1',
      },
    ],
  };

  const hrvRhrOptions = {
    ...chartOptions,
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        position: 'left' as const,
        title: { display: true, text: 'HRV (ms)', color: '#14b8a6' },
      },
      y1: {
        ...chartOptions.scales.y,
        position: 'right' as const,
        grid: { drawOnChartArea: false },
        title: { display: true, text: 'Resting HR (bpm)', color: '#f97316' },
      },
    },
  };

  // ===== Blood Pressure Chart =====
  const bpData = {
    labels,
    datasets: [
      {
        label: 'Systolic',
        data: sortedMetrics.map(m => m.bloodPressureSystolic ?? null),
        borderColor: '#ef4444',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      },
      {
        label: 'Diastolic',
        data: sortedMetrics.map(m => m.bloodPressureDiastolic ?? null),
        borderColor: '#3b82f6',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        fill: false,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      },
    ],
  };

  // ===== Glucose Chart =====
  const glucoseData = {
    labels,
    datasets: [
      {
        label: 'Glucose (mg/dL)',
        data: sortedMetrics.map(m => m.glucoseLevel ?? null),
        borderColor: '#a855f7',
        backgroundColor: 'rgba(168, 85, 247, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      },
    ],
  };

  // ===== Steps Chart =====
  const stepsData = {
    labels,
    datasets: [
      {
        label: 'Steps',
        data: sortedMetrics.map(m => m.stepsYesterday ?? null),
        backgroundColor: 'rgba(16, 185, 129, 0.6)',
        borderColor: '#10b981',
        borderWidth: 1,
      },
    ],
  };

  // ===== Sleep Chart =====
  const sleepData = {
    labels,
    datasets: [
      {
        label: 'Sleep Hours',
        data: sortedMetrics.map(m => m.sleepHours ?? null),
        backgroundColor: 'rgba(99, 102, 241, 0.6)',
        borderColor: '#6366f1',
        borderWidth: 1,
      },
    ],
  };

  // ===== Wellbeing Chart =====
  const wellbeingData = {
    labels,
    datasets: [
      {
        label: 'Wellbeing Score',
        data: sortedMetrics.map(m => m.wellbeingScore ?? null),
        borderColor: '#14b8a6',
        backgroundColor: 'rgba(20, 184, 166, 0.15)',
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        spanGaps: true,
      },
    ],
  };

  // ===== PED Anabolic Weekly Load (Injectable AAS + Oral AAS only) =====
  const pedWeeklyLoad = useMemo(() => {
    // Filter actual logs to anabolic compounds only
    const anabolicLogs = pedLogs.filter((log: any) =>
      log.compound?.type === 'InjectableAAS' || log.compound?.type === 'OralAAS'
    );

    if (anabolicLogs.length === 0 && cycles.length === 0) return null;

    // --- Build actual weekly totals per compound ---
    const weeklyData: Record<string, Record<string, number>> = {};
    const weekMondayMap: Record<string, Date> = {}; // weekKey → Monday Date
    const compoundNames = new Set<string>();

    anabolicLogs.forEach((log: any) => {
      const date = new Date(log.date);
      const day = date.getDay();
      const monday = new Date(date);
      monday.setDate(date.getDate() - ((day + 6) % 7));
      const weekKey = format(monday, 'MMM d');

      if (!weeklyData[weekKey]) weeklyData[weekKey] = {};
      weekMondayMap[weekKey] = monday;
      const compoundName = log.compound?.shortName || log.compound?.name || 'Unknown';
      compoundNames.add(compoundName);
      weeklyData[weekKey][compoundName] = (weeklyData[weekKey][compoundName] || 0) + (log.doseAmount || 0);
    });

    // --- Also seed week keys from cycle date ranges (clamped to the analytics window) ---
    const windowStart = new Date(startDate);
    const windowEnd   = new Date(endDate);

    cycles.forEach((cycle: any) => {
      const cycleStart = new Date(cycle.startDate);
      const cycleEnd   = cycle.endDate ? new Date(cycle.endDate) : new Date();

      // Clamp to the analytics time-range window
      const effectiveStart = cycleStart > windowStart ? cycleStart : windowStart;
      const effectiveEnd   = cycleEnd   < windowEnd   ? cycleEnd   : windowEnd;

      // Skip this cycle if it has no overlap with the visible window
      if (effectiveStart > effectiveEnd) return;

      // Snap effectiveStart back to the Monday of that week
      const cur = new Date(effectiveStart);
      cur.setDate(cur.getDate() - ((cur.getDay() + 6) % 7));

      while (cur <= effectiveEnd) {
        const weekKey = format(cur, 'MMM d');
        if (!weeklyData[weekKey]) weeklyData[weekKey] = {};
        if (!weekMondayMap[weekKey]) weekMondayMap[weekKey] = new Date(cur);
        cur.setDate(cur.getDate() + 7);
      }
    });

    const weekLabels = Object.keys(weeklyData).sort((a, b) =>
      (weekMondayMap[a]?.getTime() ?? 0) - (weekMondayMap[b]?.getTime() ?? 0)
    );

    // --- Planned load per week from cycle details ---
    const getPlannedLoad = (monday: Date): number => {
      const weekEnd = new Date(monday);
      weekEnd.setDate(weekEnd.getDate() + 6);
      const activeCycle = cycles.find((c: any) => {
        const s = new Date(c.startDate);
        const e = c.endDate ? new Date(c.endDate) : new Date('2099-12-31');
        return s <= weekEnd && e >= monday;
      });
      if (!activeCycle) return 0;
      return (activeCycle.details ?? [])
        .filter((d: any) => d.compound?.type === 'InjectableAAS' || d.compound?.type === 'OralAAS')
        .reduce((sum: number, d: any) => sum + (d.weeklyTotal ?? 0), 0);
    };

    // --- Bar datasets (actual, per compound) ---
    const colors = ['#d4a843', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#14b8a6', '#ec4899'];
    const barDatasets = Array.from(compoundNames).map((name, i) => ({
      type: 'bar' as const,
      label: name,
      data: weekLabels.map(week => weeklyData[week][name] || 0),
      backgroundColor: colors[i % colors.length] + '80',
      borderColor: colors[i % colors.length],
      borderWidth: 1,
      stack: 'actual',
      order: 1,
    }));

    // --- Line dataset (planned from cycle) ---
    const plannedDataset = {
      type: 'line' as const,
      label: 'Planned (Cycle)',
      data: weekLabels.map(week => getPlannedLoad(weekMondayMap[week])),
      borderColor: '#ffffff',
      backgroundColor: 'transparent',
      borderWidth: 2,
      borderDash: [6, 3],
      pointRadius: 4,
      pointBackgroundColor: '#ffffff',
      fill: false,
      tension: 0,
      order: 0,
    };

    const datasets: any[] = [...barDatasets, plannedDataset];
    return { labels: weekLabels, datasets };
  }, [pedLogs, cycles, startDate, endDate]);

  // ===== Per-Compound AAS Charts (daily dose bars + 7d rolling actual vs planned) =====
  const aasCharts = useMemo(() => {
    const rangeStart = new Date(aasStartDate);
    const rangeEnd = new Date(aasEndDate);
    const allDates: Date[] = [];
    const cur = new Date(rangeStart);
    while (cur <= rangeEnd) {
      allDates.push(new Date(cur));
      cur.setDate(cur.getDate() + 1);
    }
    if (allDates.length === 0) return null;

    // Build compound map from actual logs
    const compoundMap: Record<string, {
      compoundId: string;
      name: string;
      shortName: string;
      type: string;
      logsByDate: Record<string, number>;
    }> = {};

    aasLogs.forEach((log: any) => {
      if (log.compound?.type !== 'InjectableAAS' && log.compound?.type !== 'OralAAS') return;
      const id = log.compoundId;
      if (!compoundMap[id]) {
        compoundMap[id] = {
          compoundId: id,
          name: log.compound?.name ?? 'Unknown',
          shortName: log.compound?.shortName ?? log.compound?.name ?? 'Unknown',
          type: log.compound?.type ?? '',
          logsByDate: {},
        };
      }
      const dateKey = format(new Date(log.date), 'yyyy-MM-dd');
      compoundMap[id].logsByDate[dateKey] = (compoundMap[id].logsByDate[dateKey] ?? 0) + log.doseAmount;
    });

    // Also seed compounds from cycles that overlap the range (so planned line shows even with no logs)
    cycles.forEach((cycle: any) => {
      const cycleStart = new Date(cycle.startDate);
      const cycleEnd = cycle.endDate ? new Date(cycle.endDate) : new Date('2099-12-31');
      if (cycleStart > rangeEnd || cycleEnd < rangeStart) return;
      (cycle.details ?? []).forEach((d: any) => {
        if (d.compound?.type !== 'InjectableAAS' && d.compound?.type !== 'OralAAS') return;
        const id = d.compoundId;
        if (!compoundMap[id]) {
          compoundMap[id] = {
            compoundId: id,
            name: d.compound?.name ?? 'Unknown',
            shortName: d.compound?.shortName ?? d.compound?.name ?? 'Unknown',
            type: d.compound?.type ?? '',
            logsByDate: {},
          };
        }
      });
    });

    const compounds = Object.values(compoundMap);
    if (compounds.length === 0) return null;

    // Planned daily dose for a compound on a given date (sum across any overlapping cycles)
    const getPlannedDailyDose = (compoundId: string, date: Date): number => {
      let total = 0;
      cycles.forEach((cycle: any) => {
        const s = new Date(cycle.startDate);
        const e = cycle.endDate ? new Date(cycle.endDate) : new Date('2099-12-31');
        if (date >= s && date <= e) {
          const detail = (cycle.details ?? []).find((d: any) => d.compoundId === compoundId);
          if (detail) total += (detail.weeklyTotal ?? 0) / 7;
        }
      });
      return total;
    };

    const compoundColors = ['#d4a843', '#ef4444', '#3b82f6', '#22c55e', '#a855f7', '#f97316', '#14b8a6', '#ec4899'];
    const dateLabels = allDates.map(d => format(d, 'MMM d'));

    return compounds.map((comp, idx) => {
      const color = compoundColors[idx % compoundColors.length];

      const dailyActual = allDates.map(d => comp.logsByDate[format(d, 'yyyy-MM-dd')] ?? 0);

      const rollingActual = allDates.map((_, i) =>
        allDates.slice(Math.max(0, i - 6), i + 1)
          .reduce((sum, d) => sum + (comp.logsByDate[format(d, 'yyyy-MM-dd')] ?? 0), 0)
      );

      const rollingPlanned = allDates.map((_, i) =>
        allDates.slice(Math.max(0, i - 6), i + 1)
          .reduce((sum, d) => sum + getPlannedDailyDose(comp.compoundId, d), 0)
      );

      const hasPlannedData = rollingPlanned.some(v => v > 0);

      const datasets: any[] = [
        {
          type: 'bar' as const,
          label: 'Daily Dose (mg)',
          data: dailyActual,
          backgroundColor: color + '55',
          borderColor: color,
          borderWidth: 1,
          order: 2,
        },
        {
          type: 'line' as const,
          label: '7d Rolling Actual (mg)',
          data: rollingActual,
          borderColor: color,
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.3,
          pointRadius: 0,
          borderWidth: 2.5,
          spanGaps: true,
          order: 1,
        },
      ];

      if (hasPlannedData) {
        datasets.push({
          type: 'line' as const,
          label: '7d Rolling Planned (mg)',
          data: rollingPlanned,
          borderColor: '#ffffff',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0,
          pointRadius: 0,
          borderWidth: 2,
          borderDash: [6, 3],
          spanGaps: true,
          order: 0,
        });
      }

      return {
        compoundId: comp.compoundId,
        name: comp.name,
        shortName: comp.shortName,
        type: comp.type,
        labels: dateLabels,
        datasets,
      };
    });
  }, [aasLogs, cycles, aasStartDate, aasEndDate]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-gray-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gold-500 mb-2">Analytics & Trends</h1>
          <p className="text-gray-400">Visualize your progress over time</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <select
            value={selectedDays}
            onChange={e => setSelectedDays(e.target.value === 'custom' ? 'custom' : Number(e.target.value) as PresetDays)}
            className="bg-dark-700 text-white text-sm font-medium px-4 py-2 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500 cursor-pointer"
          >
            {([7, 14, 30, 60, 90, 180, 365] as PresetDays[]).map(d => (
              <option key={d} value={d}>{d === 365 ? '1 Year' : `${d} Days`}</option>
            ))}
            <option value="custom">Custom Range</option>
          </select>

          {selectedDays === 'custom' && (
            <div className="flex items-center gap-2 text-sm">
              <input
                type="date"
                value={customStart}
                max={customEnd}
                onChange={e => setCustomStart(e.target.value)}
                className="bg-dark-700 text-white px-3 py-1.5 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={customEnd}
                min={customStart}
                max={format(new Date(), 'yyyy-MM-dd')}
                onChange={e => setCustomEnd(e.target.value)}
                className="bg-dark-700 text-white px-3 py-1.5 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500"
              />
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
          <SummaryCard
            label="Weight Δ"
            value={`${summary.weightChange > 0 ? '+' : ''}${summary.weightChange.toFixed(1)}`}
            unit="lbs"
            color={summary.weightChange > 0 ? 'text-yellow-400' : 'text-blue-400'}
          />
          <SummaryCard
            label="Avg HRV"
            value={summary.avgHrv?.toString() ?? 'N/A'}
            unit="ms"
            color="text-teal-400"
          />
          <SummaryCard
            label="Avg Resting HR"
            value={summary.avgRestingHr?.toString() ?? 'N/A'}
            unit="bpm"
            color="text-orange-400"
          />
          <SummaryCard
            label="Avg BP"
            value={summary.avgSystolic ? `${summary.avgSystolic}/${summary.avgDiastolic}` : 'N/A'}
            unit=""
            color={summary.avgSystolic && summary.avgSystolic > 140 ? 'text-red-400' : 'text-white'}
          />
          <SummaryCard
            label="Avg Sleep"
            value={summary.avgSleep || 'N/A'}
            unit="hrs"
            color="text-indigo-400"
          />
          <SummaryCard
            label="Avg Wellbeing"
            value={summary.avgWellbeing || 'N/A'}
            unit="/10"
            color="text-teal-400"
          />
          <SummaryCard
            label="Avg Steps"
            value={summary.avgSteps?.toLocaleString() ?? 'N/A'}
            unit="/day"
            color="text-emerald-400"
          />
          <SummaryCard
            label="Data Points"
            value={`${sortedMetrics.length}`}
            unit="entries"
            color="text-gray-300"
          />
        </div>
      )}

      {sortedMetrics.length === 0 ? (
        <div className="bg-dark-800 rounded-lg p-12 text-center">
          <div className="text-4xl mb-4">📊</div>
          <p className="text-gray-400 text-lg mb-2">No metrics data yet</p>
          <p className="text-gray-500">Start logging daily metrics to see your trends here.</p>
          <a
            href="/metrics/entry"
            className="inline-block mt-4 px-6 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-400"
          >
            Log Metrics
          </a>
        </div>
      ) : (
        <>
          {/* Weight Trend - Full Width */}
          <ChartCard title="Weight Trend" subtitle="Daily weight & 7-day smoothed trend">
            <div className="h-80">
              <Line data={bodyCompData} options={chartOptions} />
            </div>
          </ChartCard>

          {/* HRV & Resting HR + Blood Pressure - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="HRV & Resting HR" subtitle="Recovery indicators over time">
              <div className="h-64">
                <Line data={hrvRhrData} options={hrvRhrOptions as any} />
              </div>
            </ChartCard>

            <ChartCard title="Blood Pressure" subtitle="Systolic / Diastolic">
              <div className="h-64">
                <Line data={bpData} options={chartOptions} />
              </div>
            </ChartCard>
          </div>

          {/* Glucose & Sleep - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Glucose" subtitle="Blood sugar levels">
              <div className="h-64">
                <Line data={glucoseData} options={chartOptions} />
              </div>
            </ChartCard>

            <ChartCard title="Sleep" subtitle="Hours per night">
              <div className="h-64">
                <Bar data={sleepData} options={chartOptions} />
              </div>
            </ChartCard>
          </div>

          {/* Wellbeing & Steps - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartCard title="Wellbeing Score" subtitle="Overall daily feeling (1-10)">
              <div className="h-64">
                <Line data={wellbeingData} options={{
                  ...chartOptions,
                  scales: {
                    ...chartOptions.scales,
                    y: {
                      ...chartOptions.scales.y,
                      min: 0,
                      max: 10,
                    },
                  },
                }} />
              </div>
            </ChartCard>

            <ChartCard title="Steps" subtitle="Steps per day">
              <div className="h-64">
                <Bar data={stepsData} options={chartOptions} />
              </div>
            </ChartCard>
          </div>

          {/* PED Anabolic Weekly Load */}
          {pedWeeklyLoad && pedWeeklyLoad.datasets.length > 0 && (
            <ChartCard
              title="Anabolic Load — Actual vs Planned"
              subtitle="Weekly Injectable AAS + Oral AAS · stacked bars = logged doses · dashed line = cycle plan"
            >
              <div className="h-80">
                <Bar
                  data={pedWeeklyLoad as any}
                  options={{
                    ...chartOptions,
                    scales: {
                      ...chartOptions.scales,
                      x: {
                        ...chartOptions.scales.x,
                        stacked: true,
                      },
                      y: {
                        ...chartOptions.scales.y,
                        stacked: true, // required so compound bars stack correctly; line type is exempt
                        ticks: {
                          ...chartOptions.scales.y.ticks,
                          callback: (v: any) => `${v}mg`,
                        },
                      },
                    },
                  } as any}
                />
              </div>
            </ChartCard>
          )}

          {/* AAS Per-Compound Tracking */}
          {aasCharts && aasCharts.length > 0 && (
            <>
              <div className="pt-2 flex justify-between items-start gap-4">
                <div>
                  <h2 className="text-2xl font-bold text-gold-500 mb-1">AAS Compound Tracking</h2>
                  <p className="text-sm text-gray-400">Bars = daily logged dose · solid line = 7-day rolling actual · dashed = 7-day rolling planned. Spot dosing discrepancies against your cycle plan.</p>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <select
                    value={aasSelectedDays}
                    onChange={e => setAasSelectedDays(e.target.value === 'custom' ? 'custom' : Number(e.target.value) as PresetDays)}
                    className="bg-dark-700 text-white text-sm font-medium px-4 py-2 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500 cursor-pointer"
                  >
                    {([7, 14, 30, 60, 90, 180, 365] as PresetDays[]).map(d => (
                      <option key={d} value={d}>{d === 365 ? '1 Year' : `${d} Days`}</option>
                    ))}
                    <option value="custom">Custom Range</option>
                  </select>
                  {aasSelectedDays === 'custom' && (
                    <div className="flex items-center gap-2 text-sm">
                      <input
                        type="date"
                        value={aasCustomStart}
                        max={aasCustomEnd}
                        onChange={e => setAasCustomStart(e.target.value)}
                        className="bg-dark-700 text-white px-3 py-1.5 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500"
                      />
                      <span className="text-gray-500">to</span>
                      <input
                        type="date"
                        value={aasCustomEnd}
                        min={aasCustomStart}
                        max={format(new Date(), 'yyyy-MM-dd')}
                        onChange={e => setAasCustomEnd(e.target.value)}
                        className="bg-dark-700 text-white px-3 py-1.5 rounded-lg border border-dark-600 focus:outline-none focus:border-gold-500"
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {aasCharts.map((chart) => (
                  <ChartCard
                    key={chart.compoundId}
                    title={chart.shortName}
                    subtitle={`${chart.name} · ${chart.type === 'InjectableAAS' ? 'Injectable AAS' : 'Oral AAS'}`}
                  >
                    <div className="h-72">
                      <Bar
                        data={{ labels: chart.labels, datasets: chart.datasets } as any}
                        options={{
                          ...chartOptions,
                          scales: {
                            ...chartOptions.scales,
                            x: { ...chartOptions.scales.x, stacked: false },
                            y: {
                              ...chartOptions.scales.y,
                              stacked: false,
                              beginAtZero: true,
                              ticks: {
                                ...chartOptions.scales.y.ticks,
                                callback: (v: any) => `${v}mg`,
                              },
                            },
                          },
                        } as any}
                      />
                    </div>
                  </ChartCard>
                ))}
              </div>
            </>
          )}

          {/* Phase Comparison */}
          {cycles.length > 0 && (
            <div className="bg-dark-800 rounded-lg p-6">
              <h2 className="text-xl font-bold text-gold-500 mb-2">Cycle Overview</h2>
              <p className="text-sm text-gray-400 mb-4">Your PED cycles and their duration</p>
              <div className="space-y-3">
                {cycles.map((cycle: any) => {
                  const start = new Date(cycle.startDate);
                  const end = cycle.endDate ? new Date(cycle.endDate) : new Date();
                  const weeks = Math.round((end.getTime() - start.getTime()) / (7 * 24 * 60 * 60 * 1000));
                  const totalWeekly = (cycle.details ?? [])
                    .filter((d: any) => d.compound?.type === 'InjectableAAS' || d.compound?.type === 'OralAAS')
                    .reduce((sum: number, d: any) => sum + (d.weeklyTotal || 0), 0);

                  return (
                    <div key={cycle.cycleId} className="flex justify-between items-center bg-dark-700 rounded-lg p-4">
                      <div>
                        <div className="font-semibold text-white">{cycle.name}</div>
                        <div className="text-sm text-gray-400">
                          {format(start, 'MMM d, yyyy')} → {cycle.endDate ? format(end, 'MMM d, yyyy') : 'Ongoing'}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-gold-400 font-bold">{weeks} weeks</div>
                        <div className="text-sm text-gray-400">
                          {cycle.details?.length || 0} compounds • {totalWeekly}mg/week total
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Helper components
const SummaryCard: React.FC<{ label: string; value: string; unit: string; color: string }> = ({
  label, value, unit, color,
}) => (
  <div className="bg-dark-800 rounded-lg p-4 text-center">
    <div className="text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</div>
    <div className={`text-xl font-bold ${color}`}>
      {value}
      <span className="text-xs text-gray-500 ml-1">{unit}</span>
    </div>
  </div>
);

const ChartCard: React.FC<{ title: string; subtitle: string; children: React.ReactNode }> = ({
  title, subtitle, children,
}) => (
  <div className="bg-dark-800 rounded-lg p-6">
    <h2 className="text-xl font-bold text-gold-500 mb-1">{title}</h2>
    <p className="text-sm text-gray-400 mb-4">{subtitle}</p>
    {children}
  </div>
);

export default Analytics;
